import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ShieldCheck,
  Lock,
  Users,
  Eye,
  Database,
  Heart,
  AlertTriangle,
  UserCheck,
  Trash2,
  MessageCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/Header';
import { openSupportEmail } from '@/lib/support';

interface SectionProps {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ icon: Icon, title, children }) => (
  <section className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm">
    <div className="flex items-start gap-4 mb-4">
      <div className="flex-shrink-0 w-11 h-11 rounded-full bg-gradient-to-br from-fuchsia-100 to-pink-100 flex items-center justify-center">
        <Icon className="w-5 h-5 text-fuchsia-600" />
      </div>
      <h2 className="font-serif text-xl sm:text-2xl font-bold text-slate-900 leading-tight pt-1.5">
        {title}
      </h2>
    </div>
    <div className="prose prose-slate max-w-none text-slate-700 text-[15px] leading-relaxed space-y-3 sm:pl-[60px]">
      {children}
    </div>
  </section>
);

const PrivacySafety: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 flex flex-col">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-slate-100 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <button onClick={() => navigate('/')} aria-label="KinsCircle home">
            <Logo />
          </button>
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="rounded-full gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {/* Hero */}
        <div className="text-center mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-fuchsia-50 border border-fuchsia-200 text-fuchsia-700 text-xs font-semibold mb-5">
            <ShieldCheck className="w-3.5 h-3.5" />
            Privacy &amp; Safety
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 leading-tight mb-4">
            Your family's story,
            <br />
            <span className="bg-gradient-to-r from-fuchsia-600 to-pink-500 bg-clip-text text-transparent">
              kept safe with you.
            </span>
          </h1>
          <p className="text-slate-600 text-base sm:text-lg max-w-xl mx-auto">
            KinsCircle is a private place for your relatives, memories, and milestones.
            Here is how we protect them — and the simple rules that keep every circle kind.
          </p>
          <p className="text-xs text-slate-400 mt-4">
            Last updated April 26, 2026
          </p>
        </div>

        <div className="space-y-5 sm:space-y-6">
          <Section icon={Lock} title="Your circle is private by default">
            <p>
              Every family circle on KinsCircle is private. The people, photos, stories,
              and life events you add are only visible to relatives you have personally
              invited — not to the public, not to search engines, and not to other
              KinsCircle families.
            </p>
            <p>
              When you upload a memory you can choose who sees it: <strong>Family only</strong>,
              <strong> Private</strong> (just you), or a <strong>Shared circle</strong> of
              specific relatives. Privacy choices are respected everywhere — timeline,
              gallery, and message board.
            </p>
          </Section>

          <Section icon={Database} title="What we collect, and why">
            <p>
              We collect only what's needed to help your family connect and remember:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-fuchsia-500">
              <li>Account details — name, email, and an encrypted password.</li>
              <li>Family content — relatives you add, photos, videos, stories, dates, and locations you choose to share.</li>
              <li>Activity — comments, reactions, and message board posts inside your circle.</li>
              <li>Basic technical data — device type and session info, to keep the app fast and secure.</li>
            </ul>
            <p>
              We do not sell your data. We do not use your family photos to train AI models.
              We do not advertise inside KinsCircle.
            </p>
          </Section>

          <Section icon={Eye} title="Who can see what">
            <p>
              Visibility on KinsCircle follows a simple rule: <strong>you control the circle, the circle controls the content.</strong>
            </p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-fuchsia-500">
              <li><strong>Family only</strong> — visible to every invited member of your circle.</li>
              <li><strong>Shared circle</strong> — visible only to relatives you specifically tag.</li>
              <li><strong>Private</strong> — visible only to you, the uploader.</li>
            </ul>
            <p>
              Family owners and admins can manage membership, but cannot change a memory's
              privacy setting on your behalf without your permission.
            </p>
          </Section>

          <Section icon={Users} title="Adding relatives respectfully">
            <p>
              Family trees often include people who are not on KinsCircle yet — including
              children, elders, and loved ones who have passed. When you add a relative:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-fuchsia-500">
              <li>Only share details (birthdays, photos, stories) you have permission to share.</li>
              <li>For living adults, invite them so they can review and edit their own profile.</li>
              <li>For children, only family members in their circle should be able to view their profile.</li>
              <li>For relatives who have passed, treat their memory and image with the same care you would offline.</li>
            </ul>
          </Section>

          <Section icon={Heart} title="Memories, comments &amp; kindness">
            <p>
              Family timelines are emotional spaces. When commenting on a relative's photo,
              video, or life event, please:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-fuchsia-500">
              <li>Be kind — assume good intent and write as if the person is reading.</li>
              <li>Add detail, not drama — corrections, dates, names, and stories are welcome.</li>
              <li>Respect privacy — don't share screenshots of your circle outside of it.</li>
              <li>Ask before tagging — especially when adding someone to a sensitive memory.</li>
            </ul>
          </Section>

          <Section icon={UserCheck} title="Your responsibilities">
            <p>
              KinsCircle works because every member helps protect the circle. As a member you agree to:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-fuchsia-500">
              <li>Keep your password secret and use a strong, unique one.</li>
              <li>Only invite people you actually know and trust as family.</li>
              <li>Upload content you have the right to share.</li>
              <li>Report harmful or inappropriate content so we can act quickly.</li>
            </ul>
          </Section>

          <Section icon={Trash2} title="Editing &amp; deleting your data">
            <p>
              You can edit or remove any memory, story, or comment you have added — at any
              time, from the relative's profile or your account page.
            </p>
            <p>
              You can permanently delete your account from <strong>Account settings → Danger zone</strong>.
              When you delete your account we remove your profile, your authored memories, and
              cancel any active subscription. Memories that other family members have built on
              top of (for example, comments on a shared event) may remain in the family circle
              under an anonymized author label.
            </p>
          </Section>

          <Section icon={ShieldCheck} title="How we keep things secure">
            <p>
              All traffic between your device and KinsCircle is encrypted in transit (HTTPS).
              Passwords are stored as one-way hashes and are never visible to staff. Photos
              and videos are stored in access-controlled cloud storage and served only to
              authenticated members of your circle.
            </p>
            <p>
              We log and monitor unusual sign-in activity, and we will notify you by email
              if we detect anything that requires your attention.
            </p>
          </Section>

          <Section icon={AlertTriangle} title="Reporting a problem">
            <p>
              If something on KinsCircle feels unsafe — a misused photo, harassment in a
              comment thread, an account that shouldn't be in your circle, or anything else —
              please reach out. Our team responds to every report personally.
            </p>
            <div className="pt-1">
              <Button
                onClick={openSupportEmail}
                className="rounded-full bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600 text-white gap-2"
              >
                <MessageCircle className="w-4 h-4" />
                Contact Support
              </Button>
            </div>
          </Section>
        </div>

        <div className="mt-10 sm:mt-14 text-center text-sm text-slate-500">
          <p>
            This page summarizes the principles we live by. The full Terms and Privacy
            Policy document everything in detail and is binding for all members.
          </p>
          <p className="mt-3 italic text-slate-400">
            
          </p>
        </div>
      </main>

      
    </div>
  );
};

export default PrivacySafety;
